import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { animated, useSpring } from 'react-spring';
import { styled } from 'styled-components';
import { layout, space } from 'styled-system';
import { onEnterPress } from '@hivebrite/utils';
import { Box } from '@design-system/box';
import { LinkSimple } from '@design-system/link';
import { focusOutline, FONT_BRIGHTNESS_MAPPING, useTheme, } from '@design-system/theme';
import { PillIcon } from './components/PillIcon';
import { PILL_STATUS_PROPS } from './constants';
const AnimatedBox = animated(Box);
const getBorder = (theme) => `1px solid ${theme.colors.horizon}`;
const PillWrapper = styled(AnimatedBox).attrs(() => ({
    font: 'footnote-asphalt',
    fontWeight: 0,
    minHeight: '24px',
    minWidth: 0,
    px: 2,
})) `
  ${focusOutline}
  ${space}
  ${layout}
  align-items: center;
  border-radius: 5px;
  border: ${({ theme }) => getBorder(theme)};
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'auto')};
  display: flex;
  justify-content: center;
  pointer-events: ${({ $isClickable }) => ($isClickable ? 'all' : 'none')};
  width: fit-content;
`;
const PillContent = styled(Box) `
  color: inherit;
  display: block;
  font-weight: inherit;
  font: inherit;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: break-spaces;
`;
const LinkWrapper = styled(LinkSimple) `
  border-radius: 5px;
  display: flex;
  overflow: hidden;
`;
const handleOnClick = (handler) => handler ? onEnterPress(handler) : undefined;
const getTabIndex = (func) => (func ? 0 : -1);
export const Pill = (_a) => {
    var { children, href, iconName, iconType, onClick, status } = _a, otherProps = __rest(_a, ["children", "href", "iconName", "iconType", "onClick", "status"]);
    const theme = useTheme();
    const { frameBackgroundColor, frameFontBrightness } = theme.themeVariables;
    const isClickable = Boolean(href || onClick);
    const initialStyle = useMemo(() => {
        if (status) {
            const statusColors = PILL_STATUS_PROPS[status];
            return {
                backgroundColor: theme.colors.white,
                borderColor: theme.colors[statusColors.borderColor],
                color: theme.colors.asphalt,
            };
        }
        return {
            backgroundColor: frameBackgroundColor,
            borderColor: theme.colors.horizon,
            color: theme.colors[FONT_BRIGHTNESS_MAPPING.asphalt[frameFontBrightness]],
        };
    }, [frameBackgroundColor, frameFontBrightness, status, theme]);
    const hoverStyle = useMemo(() => {
        if (status) {
            return initialStyle;
        }
        return {
            backgroundColor: theme.colors.primaryDark,
            borderColor: theme.colors.primaryDark,
            color: theme.colors.white,
        };
    }, [initialStyle, status, theme]);
    const [spring, springApi] = useSpring(() => initialStyle);
    const onMouseLeave = useCallback(() => springApi.start(initialStyle), [initialStyle, springApi]);
    const onMouseEnter = useCallback(() => springApi.start(hoverStyle), [hoverStyle, springApi]);
    useEffect(() => {
        springApi.start(initialStyle);
    }, [initialStyle, springApi]);
    return href ? (_jsx(LinkWrapper, { to: href, children: _jsxs(PillWrapper, Object.assign({ "$isClickable": isClickable, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, style: spring }, otherProps, { children: [iconName ? (_jsx(PillIcon, { iconName: iconName, iconType: iconType, status: status })) : null, _jsx(PillContent, { children: children })] })) })) : (_jsxs(PillWrapper, Object.assign({ "$isClickable": isClickable, onClick: onClick, onKeyPress: handleOnClick(onClick), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, style: spring, tabIndex: getTabIndex(onClick) }, otherProps, { children: [iconName ? (_jsx(PillIcon, { iconName: iconName, iconType: iconType, status: status })) : null, _jsx(PillContent, { children: children })] })));
};
//# sourceMappingURL=Pill.js.map